/**
 * ChartOfAccountFormModal — Ustawienia → Plan kont (create/edit pojedynczego konta).
 *
 * Firma NIE jest polem formularza — kontekst firmy ustala rodzic (`ChartOfAccountsTab`,
 * z top-bara albo lokalnego pickera trybu "Wszystkie firmy"); backend ma `companyId`
 * immutable po utworzeniu (mirror `chart_of_accounts` API — patrz nagłówek
 * `apps/api/app/api/chart-of-accounts/route.ts`).
 *
 * Walidacja klienta:
 * - accountNumber: non-empty, max 30 znaków
 * - name: non-empty, max 255 znaków
 * - accountType: zawsze ustawiony (domyślnie 'bilansowe')
 *
 * `parentOptions` — spłaszczone drzewo kont TEJ SAMEJ firmy (z wciętym labelem wg
 * głębokości), z wykluczonym kontem edytowanym i jego potomkami (unika oczywistego
 * cyklu w UI; backend i tak re-waliduje `PARENT_CYCLE`/`PARENT_NOT_IN_COMPANY`).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';

export type AccountType = 'bilansowe' | 'wynikowe' | 'pozabilansowe';
export type NormalSide = '' | 'debit' | 'credit';

export interface ChartOfAccountFormValues {
  accountNumber: string;
  name: string;
  /** '' = brak rodzica (konto korzenia/syntetyczne najwyższego poziomu). */
  parentId: string;
  accountType: AccountType;
  isSettlement: boolean;
  normalSide: NormalSide;
  isPostable: boolean;
  isActive: boolean;
  isBlocked: boolean;
}

export interface ChartOfAccountParentOption {
  id: string;
  accountNumber: string;
  name: string;
  /** Głębokość w drzewie — steruje wizualnym wcięciem opcji w select. */
  depth: number;
}

export function emptyChartOfAccountFormValues(
  parentId: string | null = null,
): ChartOfAccountFormValues {
  return {
    accountNumber: '',
    name: '',
    parentId: parentId ?? '',
    accountType: 'bilansowe',
    isSettlement: false,
    normalSide: '',
    isPostable: true,
    isActive: true,
    isBlocked: false,
  };
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: ChartOfAccountFormValues;
  saving?: boolean;
  parentOptions: ChartOfAccountParentOption[];
  onSubmit: (values: ChartOfAccountFormValues) => void;
  onClose: () => void;
}

export function ChartOfAccountFormModal({
  open,
  mode,
  initialValues,
  saving = false,
  parentOptions,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setErrors({});
    }
  }, [open, initialValues]);

  function set<K extends keyof ChartOfAccountFormValues>(
    key: K,
    value: ChartOfAccountFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.accountNumber.trim()) {
      next.accountNumber = t('tenant.chartOfAccounts.form.errors.accountNumberRequired');
    } else if (values.accountNumber.trim().length > 30) {
      next.accountNumber = t('tenant.chartOfAccounts.form.errors.accountNumberLength');
    }
    if (!values.name.trim()) {
      next.name = t('tenant.chartOfAccounts.form.errors.nameRequired');
    } else if (values.name.trim().length > 255) {
      next.name = t('tenant.chartOfAccounts.form.errors.nameLength');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({ ...values, accountNumber: values.accountNumber.trim(), name: values.name.trim() });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        mode === 'create'
          ? 'tenant.chartOfAccounts.form.titleCreate'
          : 'tenant.chartOfAccounts.form.titleEdit',
      )}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="coa-form-submit"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="chart-of-account-form">
        <FormField
          label={t('tenant.chartOfAccounts.form.accountNumber')}
          required
          error={errors.accountNumber}
        >
          <Input
            value={values.accountNumber}
            onChange={(e) => set('accountNumber', e.target.value)}
            maxLength={30}
            data-testid="coa-form-account-number"
          />
        </FormField>

        <FormField label={t('tenant.chartOfAccounts.form.name')} required error={errors.name}>
          <Input
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            maxLength={255}
            data-testid="coa-form-name"
          />
        </FormField>

        <FormField label={t('tenant.chartOfAccounts.form.parentId')}>
          <Dropdown
            value={values.parentId}
            onChange={(v) => set('parentId', v)}
            data-testid="coa-form-parent"
            options={[
              { value: '', label: t('tenant.chartOfAccounts.form.parentNone') },
              ...parentOptions.map((opt) => ({
                value: opt.id,
                label: '  '.repeat(opt.depth) + opt.accountNumber + ' — ' + opt.name,
              })),
            ]}
          />
        </FormField>

        <FormField label={t('tenant.chartOfAccounts.form.accountType')} required>
          <Dropdown
            value={values.accountType}
            onChange={(v) => set('accountType', v as AccountType)}
            data-testid="coa-form-account-type"
            options={[
              { value: 'bilansowe', label: t('tenant.chartOfAccounts.accountTypes.bilansowe') },
              { value: 'wynikowe', label: t('tenant.chartOfAccounts.accountTypes.wynikowe') },
              {
                value: 'pozabilansowe',
                label: t('tenant.chartOfAccounts.accountTypes.pozabilansowe'),
              },
            ]}
          />
        </FormField>

        <FormField label={t('tenant.chartOfAccounts.form.normalSide')}>
          <Dropdown
            value={values.normalSide}
            onChange={(v) => set('normalSide', v as NormalSide)}
            data-testid="coa-form-normal-side"
            options={[
              { value: '', label: t('tenant.chartOfAccounts.normalSides.none') },
              { value: 'debit', label: t('tenant.chartOfAccounts.normalSides.debit') },
              { value: 'credit', label: t('tenant.chartOfAccounts.normalSides.credit') },
            ]}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <Checkbox
            label={t('tenant.chartOfAccounts.form.isSettlement')}
            checked={values.isSettlement}
            onChange={(e) => set('isSettlement', e.target.checked)}
            data-testid="coa-form-is-settlement"
          />
          <Checkbox
            label={t('tenant.chartOfAccounts.form.isPostable')}
            checked={values.isPostable}
            onChange={(e) => set('isPostable', e.target.checked)}
            data-testid="coa-form-is-postable"
          />
          <Checkbox
            label={t('tenant.chartOfAccounts.form.isActive')}
            checked={values.isActive}
            onChange={(e) => set('isActive', e.target.checked)}
            data-testid="coa-form-is-active"
          />
          <Checkbox
            label={t('tenant.chartOfAccounts.form.isBlocked')}
            checked={values.isBlocked}
            onChange={(e) => set('isBlocked', e.target.checked)}
            data-testid="coa-form-is-blocked"
          />
        </div>
      </div>
    </Modal>
  );
}
